import { useState, type ReactNode } from 'react';
import type { DragInputType } from './types';

export type DragHandlePressPayload = {
  input: DragInputType;
  clientX?: number;
  clientY?: number;
};

export type DragHandleProps = {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  onPressStart?: (payload: DragHandlePressPayload) => void;
  children?: ReactNode;
};

/**
 * Dedicated drag handle that starts reorder intent without triggering row actions.
 */
export function DragHandle({
  ariaLabel = 'Reorder item',
  className = '',
  disabled = false,
  onPressStart,
  children,
}: DragHandleProps): JSX.Element {
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handlePressStart = ({
    input,
    clientX,
    clientY,
  }: DragHandlePressPayload): void => {
    if (disabled) return;

    onPressStart?.({
      input,
      clientX,
      clientY,
    });
  };

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      data-focused={isFocused ? 'true' : 'false'}
      data-dnd-drag-handle="true"
      className={`inline-flex h-8 w-8 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80 ${className}`}
      style={{ touchAction: 'none' }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();

        const resolvedInput: DragInputType =
          event.pointerType === 'touch' || event.pointerType === 'pen'
            ? 'touch'
            : 'pointer';

        handlePressStart({
          input: resolvedInput,
          clientX: event.clientX,
          clientY: event.clientY,
        });
      }}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handlePressStart({
          input: 'pointer',
          clientX: event.clientX,
          clientY: event.clientY,
        });
      }}
      onTouchStart={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const firstTouch = event.touches[0];
        handlePressStart({
          input: 'touch',
          clientX: firstTouch?.clientX,
          clientY: firstTouch?.clientY,
        });
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        event.stopPropagation();
        handlePressStart({ input: 'keyboard' });
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onBlur={() => {
        setIsFocused(false);
      }}
    >
      {children ?? <span aria-hidden>::</span>}
    </button>
  );
}
